/**
 * Authentication API client.
 *
 * OAuth 2.0 Authorization Code Flow with backend-generated JWT tokens.
 *
 * @module api/auth
 */

import type { User, AuthTokens, RefreshResponse } from '@/types'

// Storage keys
const STORAGE_KEY_ACCESS = 'org.cylian.granite.access_token'
const STORAGE_KEY_REFRESH = 'org.cylian.granite.refresh_token'
const STORAGE_KEY_EXPIRES = 'org.cylian.granite.expires_at'
const STORAGE_KEY_USER = 'org.cylian.granite.user'

// Configuration
let apiUrl: string | null = null

/**
 * Configure the auth module.
 *
 * @param options - Configuration options
 */
export function configure(options: { apiUrl: string }): void {
  apiUrl = options.apiUrl.replace(/\/$/, '')
  // Completed
}

/**
 * Get configured API URL.
 *
 * @returns API URL or null if not configured
 */
export function getApiUrl(): string | null {
  // Completed
  return apiUrl
}

// =============================================================================
// OAuth Flow
// =============================================================================

/**
 * Initiate OAuth login flow.
 * Redirects to backend which redirects to Google.
 *
 * @param redirectId - Redirect identifier configured in security.json
 */
export function login(redirectId?: string): void {
  if (!apiUrl) {
    console.error('[Auth] API URL not configured')
    return
  }

  const params = new URLSearchParams()
  if (redirectId) {
    params.set('redirect', redirectId)
  }

  const url = `${apiUrl}/@auth/google/login${params.toString() ? '?' + params.toString() : ''}`
  console.info('[Auth] Redirecting to OAuth login:', url)

  // Redirect to OAuth
  window.location.href = url
  // Completed
}

/**
 * Parse OAuth callback tokens from URL fragment.
 * Called after redirect from backend with tokens in fragment.
 *
 * @returns Parsed tokens or null if not present
 */
export function parseCallbackTokens(): AuthTokens | null {
  const hash = window.location.hash
  if (!hash || hash.length < 2) {
    // Completed
    return null
  }

  // Parse fragment parameters
  const params = new URLSearchParams(hash.substring(1))
  const accessToken = params.get('access_token')
  const refreshToken = params.get('refresh_token')
  const expiresIn = params.get('expires_in')

  if (!accessToken || !refreshToken || !expiresIn) {
    // Check for error
    const error = params.get('error')
    if (error) {
      console.error('[Auth] OAuth error:', error, params.get('error_description'))
    }
    // Completed
    return null
  }

  const expiresInSec = parseInt(expiresIn, 10)
  const expiresAt = Math.floor(Date.now() / 1000) + expiresInSec

  console.info('[Auth] Parsed callback tokens, expires in', expiresInSec, 'seconds')

  // Completed
  return {
    accessToken,
    refreshToken,
    expiresIn: expiresInSec,
    expiresAt,
  }
}

/**
 * Clear URL fragment after parsing tokens.
 */
export function clearUrlFragment(): void {
  // Replace URL without fragment
  const url = window.location.href.split('#')[0]
  window.history.replaceState(null, '', url)
  // Completed
}

// =============================================================================
// Token Management
// =============================================================================

/**
 * Refresh access token using refresh token.
 *
 * @param refreshToken - Current refresh token
 * @returns New tokens
 * @throws Error if refresh fails
 */
export async function refreshAccessToken(refreshToken: string): Promise<AuthTokens> {
  if (!apiUrl) {
    throw new Error('API URL not configured')
  }

  console.info('[Auth] Refreshing access token...')

  const response = await fetch(`${apiUrl}/@auth/google/refresh`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Refresh failed' }))
    console.error('[Auth] Token refresh failed:', error)
    throw new Error(error.error || 'Token refresh failed')
  }

  const data: RefreshResponse = await response.json()
  const expiresAt = Math.floor(Date.now() / 1000) + data.expires_in

  console.info('[Auth] Token refreshed, expires in', data.expires_in, 'seconds')

  // Completed
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresIn: data.expires_in,
    expiresAt,
  }
}

/**
 * Logout and revoke tokens.
 *
 * @param accessToken - Current access token
 */
export async function logout(accessToken: string): Promise<void> {
  if (!apiUrl) {
    console.warn('[Auth] API URL not configured, clearing local state only')
    clearStoredAuth()
    return
  }

  console.info('[Auth] Logging out...')

  try {
    const response = await fetch(`${apiUrl}/@auth/google/logout`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
      },
    })

    if (!response.ok) {
      console.warn('[Auth] Logout request failed, clearing local state')
    }
  } catch (error) {
    console.warn('[Auth] Logout request error:', error)
  }

  // Always clear local state
  clearStoredAuth()
  console.info('[Auth] Logged out')
  // Completed
}

// =============================================================================
// User Info
// =============================================================================

/**
 * Get current user info from API.
 *
 * @param accessToken - Access token
 * @returns User profile with permissions
 * @throws Error if request fails
 */
export async function getMe(accessToken: string): Promise<User> {
  if (!apiUrl) {
    throw new Error('API URL not configured')
  }

  const response = await fetch(`${apiUrl}/@auth/me`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
    },
  })

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Failed to get user info' }))
    throw new Error(error.error || 'Failed to get user info')
  }

  const user: User = await response.json()
  console.info('[Auth] Got user info:', user.email)

  // Completed
  return user
}

// =============================================================================
// Storage
// =============================================================================

/**
 * Store tokens in browser storage.
 * Access token in sessionStorage, refresh token in localStorage.
 *
 * @param tokens - Tokens to store
 * @param user - User profile to store
 */
export function storeAuth(tokens: AuthTokens, user: User): void {
  // Access token in sessionStorage (short-lived)
  sessionStorage.setItem(STORAGE_KEY_ACCESS, tokens.accessToken)
  sessionStorage.setItem(STORAGE_KEY_EXPIRES, tokens.expiresAt.toString())

  // Refresh token in localStorage (persistent)
  localStorage.setItem(STORAGE_KEY_REFRESH, tokens.refreshToken)

  // User in localStorage
  localStorage.setItem(STORAGE_KEY_USER, JSON.stringify(user))

  console.info('[Auth] Stored auth state')
  // Completed
}

/**
 * Load stored auth from browser storage.
 *
 * @returns Stored tokens and user, or null if not found
 */
export function loadStoredAuth(): { tokens: AuthTokens; user: User } | null {
  const accessToken = sessionStorage.getItem(STORAGE_KEY_ACCESS)
  const expiresAtStr = sessionStorage.getItem(STORAGE_KEY_EXPIRES)
  const refreshToken = localStorage.getItem(STORAGE_KEY_REFRESH)
  const userJson = localStorage.getItem(STORAGE_KEY_USER)

  // Need at least refresh token and user to restore
  if (!refreshToken || !userJson) {
    // Completed
    return null
  }

  try {
    const user = JSON.parse(userJson) as User
    const expiresAt = expiresAtStr ? parseInt(expiresAtStr, 10) : 0

    // If access token is missing or expired, return with null access token
    // Store will handle refresh
    const tokens: AuthTokens = {
      accessToken: accessToken || '',
      refreshToken,
      expiresIn: 0,
      expiresAt,
    }

    console.info('[Auth] Loaded stored auth for:', user.email)

    // Completed
    return { tokens, user }
  } catch (error) {
    console.warn('[Auth] Failed to parse stored auth:', error)
    clearStoredAuth()
    // Completed
    return null
  }
}

/**
 * Clear all stored auth data.
 */
export function clearStoredAuth(): void {
  sessionStorage.removeItem(STORAGE_KEY_ACCESS)
  sessionStorage.removeItem(STORAGE_KEY_EXPIRES)
  localStorage.removeItem(STORAGE_KEY_REFRESH)
  localStorage.removeItem(STORAGE_KEY_USER)
  console.info('[Auth] Cleared stored auth')
  // Completed
}

/**
 * Check if access token is expired or about to expire.
 *
 * @param expiresAt - Token expiration timestamp (seconds)
 * @param bufferSec - Buffer before expiration (default 60s)
 * @returns True if token is expired or will expire soon
 */
export function isTokenExpired(expiresAt: number, bufferSec = 60): boolean {
  const now = Math.floor(Date.now() / 1000)
  // Completed
  return expiresAt <= now + bufferSec
}

// Completed
